import * as LancerCombat from "./combat.mjs";
import * as LancerCombatant from "./combatant.mjs";
import * as LancerCombatTracker from "./lancer-combat-tracker.mjs";
import { LancerInitiativeConfigApp } from "./li-config.mjs";

import "./lancer-initiative.scss";

const MODULE = "lancer-initiative";

/**
 * @param {CombatTrackerAppearance} val
 */
export function setAppearance(val) {
  document.documentElement.style.setProperty(
    "--lancer-initiative-icon-size",
    `${val.icon_size}rem`
  );
  document.documentElement.style.setProperty("--lancer-initiative-player-color", val.player_color);
  document.documentElement.style.setProperty(
    "--lancer-initiative-friendly-color",
    val.friendly_color
  );
  document.documentElement.style.setProperty(
    "--lancer-initiative-neutral-color",
    val.neutral_color
  );
  document.documentElement.style.setProperty("--lancer-initiative-enemy-color", val.enemy_color);
  document.documentElement.style.setProperty("--lancer-initiative-secret-color", val.secret_color);
  document.documentElement.style.setProperty("--lancer-initiative-done-color", val.done_color);
  game.combats?.render();
}

Hooks.once("init", () => {
  console.log(`${MODULE} | Initializing Lancer Initiative Module`);

  // Call hooks to signal other modules of the initialization
  Hooks.callAll("LancerInitiativeInit");

  game.settings.registerMenu(MODULE, "combat-tracker-appearance", {
    name: "LANCERINITIATIVE.IconSettingsMenu",
    label: "LANCERINITIATIVE.IconSettingsMenu",
    type: LancerInitiativeConfigApp,
    restricted: false,
  });
  game.settings.register(MODULE, "combat-tracker-appearance", {
    scope: "client",
    config: false,
    type: CombatTrackerAppearance,
    onChange: setAppearance,
  });
  game.settings.register(MODULE, "activations-formula", {
    name: "LANCERINITIATIVE.SETTING.activations-formula.name",
    hint: "LANCERINITIATIVE.SETTING.activations-formula.hint",
    scope: "world",
    config: true,
    type: new foundry.data.fields.StringField({
      initial: "1",
      validate: v => foundry.dice.Roll.validate(v) && new foundry.dice.Roll(v).isDeterministic,
      validationError: "activations-formula must be a deterministic Roll formula",
    }),
  });
  game.settings.register(MODULE, "combat-tracker-sort", {
    name: "LANCERINITIATIVE.SortTracker",
    hint: "LANCERINITIATIVE.SortTrackerDesc",
    scope: "world",
    config: true,
    type: new foundry.data.fields.BooleanField({ initial: true }),
    onChange: () => game.combats?.render(),
  });
  game.settings.register(MODULE, "combat-tracker-enable-initiative", {
    name: "LANCERINITIATIVE.EnableInitiative",
    hint: "LANCERINITIATIVE.EnableInitiativeDesc",
    scope: "world",
    config: true,
    type: new foundry.data.fields.BooleanField({ initial: false }),
    onChange: () => game.combats?.render(),
  });

  // Set the css vars
  setAppearance(game.settings.get(MODULE, "combat-tracker-appearance"));
});

Hooks.once("libWrapper.Ready", () => {
  // Combat tracker wrappers
  const tracker = foundry.applications.sidebar.tabs.CombatTracker;
  tracker.PARTS.tracker.template = "modules/lancer-initiative/templates/tracker.hbs";
  tracker.DEFAULT_OPTIONS.classes ??= [];
  tracker.DEFAULT_OPTIONS.classes.push("lancer-initiative");
  Object.assign(tracker.DEFAULT_OPTIONS.actions, {
    activateCombatantTurn: LancerCombatTracker.activateCombatantTurn,
    deactivateCombatantTurn: LancerCombatTracker.deactivateCombatantTurn,
  });
  libWrapper.register(
    "lancer-initiative",
    "foundry.applications.sidebar.tabs.CombatTracker.prototype._prepareTrackerContext",
    LancerCombatTracker.prepareTrackerContext,
    "WRAPPER"
  );
  libWrapper.register(
    "lancer-initiative",
    "foundry.applications.sidebar.tabs.CombatTracker.prototype._getEntryContextOptions",
    LancerCombatTracker.getEntryContextOptions,
    "WRAPPER"
  );

  libWrapper.register(
    "lancer-initiative",
    "Combat.prototype.nextTurn",
    LancerCombat.nextTurn,
    "OVERRIDE"
  );
  libWrapper.register(
    "lancer-initiative",
    "Combat.prototype.previousTurn",
    LancerCombat.previousTurn,
    "OVERRIDE"
  );
  libWrapper.register(
    "lancer-initiative",
    "Combat.prototype.resetAll",
    LancerCombat.resetAll,
    "OVERRIDE"
  );
  Hooks.once("init", () => // Register override later to wrap the system function
    libWrapper.register(
      "lancer-initiative",
      "CONFIG.Combat.documentClass.prototype._sortCombatants",
      LancerCombat.sortCombatants,
      "MIXED"
    )
  );

  libWrapper.register(
    "lancer-initiative",
    "Combatant.prototype.prepareBaseData",
    LancerCombatant.prepareBaseData,
    "WRAPPER"
  );
});

Hooks.on("combatStart", LancerCombat.combatStart);
Hooks.on("combatRound", LancerCombat.combatRound);
Hooks.on("renderCombatTracker", LancerCombatTracker.renderCombatTracker);

class CombatTrackerAppearance extends foundry.abstract.DataModel {
  static defineSchema() {
    const fields = foundry.data.fields;
    return {
      icon: new fields.StringField({
        required: true,
        initial: "fas fa-circle-chevron-right",
        label: "LANCERINITIATIVE.Icon",
      }),
      deactivate: new fields.StringField({
        required: true,
        initial: "fas fa-circle-xmark",
        label: "LANCERINITIATIVE.DeactivateIcon",
      }),
      icon_size: new fields.NumberField({
        required: true,
        initial: 1.5,
        min: 1,
        max: 3,
        step: 0.1,
        integer: false,
        label: "LANCERINITIATIVE.IconSize",
      }),
      player_color: new fields.ColorField({
        required: true,
        initial: "#44abe0",
        label: "LANCERINITIATIVE.PartyColor",
      }),
      friendly_color: new fields.ColorField({
        required: true,
        initial: "#44abe0",
        label: "LANCERINITIATIVE.FriendlyColor",
      }),
      neutral_color: new fields.ColorField({
        required: true,
        initial: "#146464",
        label: "LANCERINITIATIVE.NeutralColor",
      }),
      enemy_color: new fields.ColorField({
        required: true,
        initial: "#d98f30",
        label: "LANCERINITIATIVE.EnemyColor",
      }),
      secret_color: new fields.ColorField({
        required: true,
        initial: "#552f8c",
        label: "LANCERINITIATIVE.SecretColor",
      }),
      done_color: new fields.ColorField({
        required: true,
        initial: "#aaaaaa",
        label: "LANCERINITIATIVE.DeactivateColor",
      }),
    };
  }
}
